import {
    NoContent,
    NotificationPayload,
    PaginationParams,
    PaginationResponse,
    ResponseType,
    TableFiltersPayload,
    TableOrder,
    UploadedDocument
} from '@/types.ts'
import axios, {parseResponseData} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {
    DisabilityProfile,
    DisabilityProfileIcfCode,
    DisabilityProfileLetter,
    Diagnosis,
    EvaluationScaleExtended,
    MedicalDevice,
    ProjectByPatient,
    ProjectExtended,
    ProjectNotesType,
    ProjectPathology,
    ProjectSender,
    ProjectSmall,
    ProjectStatusAction,
    ProjectType,
    Applicant,
    NetworkSupport,
    ActivityAutonomy,
    MobilityAutonomy,
    ParentalResponsibility,
    ProjectClinicalPresentation,
    StabilityLevel,
    ProjectClinicalCareNeed,
    ProjectPatientLivingCondition,
    ProjectRehabilitationFacility,
    AssistiveRegimeEnum,
    ProjectStatus,
    ProjectSuspendReason,
    ProjectAccessCounters,
    ProjectProgressStatus,
    ProjectCycleCount,
    ProjectsColumn,
    ProjectIndex,
    ProjectEndingReason
} from '@/features/projects/types'

export interface HttpGetProjectsOptions {
    params: PaginationParams & {
        search?: string
    }
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}

export const httpGetProjects = ({params, payload}: HttpGetProjectsOptions) =>
    axios.post('projects/index', payload, {params})

/*Export projects*/
interface HttpExportProjectsOptions {
    payload: {
        selectedProjectIds: string[]
        exceptedProjectIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: ProjectsColumn[]
        sorts?: TableOrder[]
    }
}
export const httpExportProjects = ({payload}: HttpExportProjectsOptions) =>
    axios.post('projects/export', payload, {responseType: 'blob'})

/*Delete patient*/
export const httpDeleteProject = ({urlParams}: {urlParams: {projectId: string}}) =>
    axios.delete(`projects/${urlParams.projectId}`)

// get project data
export interface HttpGetProjectOptions {
    urlParams: {
        projectId: string
    }
    params: {
        responseType: ResponseType
    }
}

// get project data
export const httpGetProject = async ({urlParams, params}: HttpGetProjectOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectExtended
    })
}

// Create project
export interface HttpCreateProjectOptions {
    payload: {
        patientId: string
        treatmentRequestId: string
        status: ProjectStatus
        startDate: string
        durationDays?: number
        actualDays?: number
        type?: ProjectType
        numberOfCycles?: ProjectCycleCount
        sector: 'outpatient' | 'domiciliary' | 'mixed'
        assistiveRegime: AssistiveRegimeEnum | null
        rehabilitativeAssistanceCommitment?: string
        expirationDate?: string
        specialistId?: string
        sender?: ProjectSender
        notificationRecipientIds?: string[]
        notificationDetails?: string
    }
}

export const httpCreateProject = async ({payload}: HttpCreateProjectOptions) => {
    const response = await axios.post('projects', payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectSmall.omit({referringDoctor: true, progressStatus: true})
    })
}

// get projects by patient
export interface HttpGetProjectsByPatientOptions {
    urlParams: {
        patientId: string
    }
    params: PaginationParams & {
        startDate?: string
        endDate?: string
        search?: string
        responseType?: ResponseType | 'index'
    }
}

export const HttpGetProjectsByPatientResponse = z.object({
    data: z.array(ProjectByPatient),
    meta: PaginationResponse
})
export type HttpGetProjectsByPatientResponse = z.infer<typeof HttpGetProjectsByPatientResponse>

export const httpGetProjectsByPatient = async ({urlParams, params}: HttpGetProjectsByPatientOptions) => {
    const response = await axios.get(`patients/${urlParams.patientId}/projects`, {
        params
    })

    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetProjectsByPatientResponse
    })
}
// get a project by patient and date
export interface HttpGetProjectsByPatientDateOptions<TResponseType extends ResponseType> {
    urlParams: {
        patientId: string
    }
    params: {
        date: string
        responseType: TResponseType
    }
}

const ProjectByPatientDateResponse = z.object({
    small: z.array(ProjectIndex).or(NoContent),
    extended: z.array(ProjectExtended).or(NoContent)
})
export type ProjectByPatientDateResponse = z.infer<typeof ProjectByPatientDateResponse>

export const httpGetProjectsByPatientDate = async <TResponseType extends ResponseType>({
    urlParams,
    params
}: HttpGetProjectsByPatientDateOptions<TResponseType>) => {
    const response = await axios.get(`patients/${urlParams.patientId}/projects/index-by-date`, {
        params
    })

    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectByPatientDateResponse.shape[params.responseType]
    })
}

// edit project scales
export interface HttpEditProjectScalesOptions {
    urlParams: {
        projectId: string
    }
    payload: EvaluationScaleExtended[]
}

export const httpEditProjectEvaluationScales = async ({urlParams, payload}: HttpEditProjectScalesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/evaluation-scales`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: EvaluationScaleExtended.array()
    })
}

// Renew project
type BaseHttpRenewTransformProjectPayload = {
    sector: 'outpatient' | 'domiciliary' | 'mixed'
    assistiveRegime: AssistiveRegimeEnum | null
    rehabilitativeAssistanceCommitment?: string
    startDate: string
    empty: boolean
}

export interface HttpRenewTransformProjectOptions {
    urlParams: {
        projectId: string
    }
    payload: NotificationPayload &
        (
            | ({
                  action: 'renew'
                  type: ProjectType
                  expirationDate: string
                  durationDays: number
                  sender: ProjectSender
                  specialistId: string
              } & BaseHttpRenewTransformProjectPayload)
            | ({
                  action: 'transform'
                  type: ProjectType
                  expirationDate: string
                  durationDays: number
                  sender: ProjectSender
                  specialistId: string
              } & BaseHttpRenewTransformProjectPayload)
            | ({
                  action: 'variation'
                  type: ProjectType
                  expirationDate: string
                  durationDays: number
                  sender: ProjectSender
                  specialistId: string
                  endDate?: string
              } & BaseHttpRenewTransformProjectPayload)
            | ({
                  action: 'resume'
                  type: ProjectType
                  expirationDate?: string
                  durationDays?: number
                  sender?: ProjectSender
                  specialistId?: string
              } & BaseHttpRenewTransformProjectPayload)
        )
}

export const httpRenewTransformProject = async ({urlParams, payload}: HttpRenewTransformProjectOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/renew-transform`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectSmall.omit({referringDoctor: true, progressStatus: true})
    })
}

// Update project status
export interface HttpUpdateProjectStatusOptions {
    urlParams: {
        projectId: string
    }
    payload: {
        status: ProjectStatusAction
    }
}
export const httpUpdateProjectStatus = async ({urlParams, payload}: HttpUpdateProjectStatusOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/status`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectExtended
    })
}

// create project notes
export interface HttpCreateProjectNotesOptions {
    urlParams: {
        projectId: string
    }
    payload: {
        note: string | null
        type: ProjectNotesType
        firstAttachmentId?: string | null
        secondAttachmentId?: string | null
    }
}

export const httpCreateProjectNotes = ({urlParams, payload}: HttpCreateProjectNotesOptions) =>
    axios.post(`projects/${urlParams.projectId}/notes`, payload)

// get project notes
export interface HttpGetProjectNotesOptions {
    urlParams: {
        projectId: string
    }
    params: {
        responseType: ResponseType
    }
}

export const HttpGetProjectNotesResponse = z.object({
    id: z.string(),
    note: z.string().nullable(),
    type: ProjectNotesType,
    firstAttachment: UploadedDocument.nullable(),
    secondAttachment: UploadedDocument.nullable()
})
export type HttpGetProjectNotesResponse = z.infer<typeof HttpGetProjectNotesResponse>

export const httpGetProjectNotes = async ({urlParams, params}: HttpGetProjectNotesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/notes`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(HttpGetProjectNotesResponse)
    })
}

// get diagnosis facilities
export interface HttpGetDiagnosesOptions {
    params: PaginationParams & {
        search?: string
    }
}

export const HttpGetDiagnosesResponse = z.object({
    data: z.array(Diagnosis),
    meta: PaginationResponse
})
export type HttpGetDiagnosesResponse = z.infer<typeof HttpGetDiagnosesResponse>

export const httpGetDiagnoses = async ({params}: HttpGetDiagnosesOptions) => {
    const response = await axios.get(`diagnoses`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetDiagnosesResponse
    })
}

// get project pathologies
export const HttpPathologiesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    })
})
export type HttpPathologiesOptions = z.infer<typeof HttpPathologiesOptions>

export const httpGetPathologies = async ({urlParams}: HttpPathologiesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/main-pathologies`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(ProjectPathology)
    })
}

// get project other pathologies
export const HttpGetProjectOtherPathologiesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    })
})
export type HttpGetProjectOtherPathologiesOptions = z.infer<typeof HttpGetProjectOtherPathologiesOptions>

export const httpGetProjectOtherPathologies = async ({urlParams}: HttpGetProjectOtherPathologiesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/other-pathologies`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(ProjectPathology.omit({startedAt: true}))
    })
}

// get project medical devices
export const HttpGetProjectMedicalDevicesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    })
})
export type HttpGetProjectMedicalDevicesOptions = z.infer<typeof HttpGetProjectMedicalDevicesOptions>

export const httpGetProjectMedicalDevices = async ({urlParams}: HttpGetProjectMedicalDevicesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/medical-devices`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(MedicalDevice)
    })
}

// store project main pathologies
export const HttpStoreProjectMainPathologiesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    }),
    payload: z.object({
        mainPathologies: z.array(ProjectPathology.pick({diagnosisId: true, startedAt: true}))
    })
})
export type HttpStoreProjectMainPathologiesOptions = z.infer<typeof HttpStoreProjectMainPathologiesOptions>

export const httpStoreProjectMainPathologies = async ({urlParams, payload}: HttpStoreProjectMainPathologiesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/main-pathologies`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(ProjectPathology)
    })
}

// store project other pathologies
export const HttpStoreProjectOtherPathologiesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    }),
    payload: z.object({
        otherPathologies: z.array(ProjectPathology.pick({diagnosisId: true}))
    })
})
export type HttpStoreProjectOtherPathologiesOptions = z.infer<typeof HttpStoreProjectOtherPathologiesOptions>

export const httpStoreProjectOtherPathologies = async ({
    urlParams,
    payload
}: HttpStoreProjectOtherPathologiesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/other-pathologies`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(ProjectPathology.pick({diagnosisId: true}))
    })
}

// store project medical devices
export const HttpStoreProjectMedicalDevicesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    }),
    payload: z.object({
        devices: z.array(MedicalDevice.pick({device: true}))
    })
})
export type HttpStoreProjectMedicalDevicesOptions = z.infer<typeof HttpStoreProjectMedicalDevicesOptions>

export const httpStoreProjectMedicalDevices = async ({urlParams, payload}: HttpStoreProjectMedicalDevicesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/medical-devices`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(MedicalDevice)
    })
}

// Update project disability profiles
export interface HttpEditProjectDisabilityProfilesOptions {
    urlParams: {
        projectId: string
    }
    payload: {
        letter: DisabilityProfileLetter
        profile: {
            icfCode: DisabilityProfileIcfCode
            type?: DisabilityProfile['type']
            qualifier1: number
            qualifier2?: number
        }[]
    }
}
export const httpEditProjectDisabilityProfiles = async ({
    urlParams,
    payload
}: HttpEditProjectDisabilityProfilesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/disability-profiles`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: DisabilityProfile.array()
    })
}

// get project socio assistance conditions
export const HttpGetProjectSocialWelfareConditionsResponse = z.object({
    id: z.string(),
    parentalResponsibility: ParentalResponsibility.nullable(),
    applicant: Applicant,
    percentagePaid: z.number(),
    sharedCarePlanning: z.boolean(),
    socialRehabilitationTreatments: z.boolean(),
    caregiver: z.boolean(),
    networkSupport: NetworkSupport.nullable(),
    activityAutonomy: ActivityAutonomy.nullable(),
    mobilityAutonomy: MobilityAutonomy.nullable()
})
export type HttpGetProjectSocialWelfareConditionsResponse = z.infer<
    typeof HttpGetProjectSocialWelfareConditionsResponse
>

export const httpGetProjectSocialWelfareConditions = async ({urlParams}: {urlParams: {projectId: string}}) => {
    const response = await axios.get(`projects/${urlParams.projectId}/social-welfare-conditions`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetProjectSocialWelfareConditionsResponse
    })
}

export const HttpStoreProjectSocialWelfareConditionsPayload = HttpGetProjectSocialWelfareConditionsResponse.pick({
    parentalResponsibility: true,
    applicant: true,
    percentagePaid: true,
    sharedCarePlanning: true,
    socialRehabilitationTreatments: true,
    networkSupport: true,
    caregiver: true,
    activityAutonomy: true,
    mobilityAutonomy: true
})
export type HttpStoreProjectSocialWelfareConditionsPayload = z.infer<
    typeof HttpStoreProjectSocialWelfareConditionsPayload
>
// update project social welfare conditions
export const httpStoreProjectSocialWelfareConditions = ({
    urlParams,
    payload
}: {
    urlParams: {projectId: string}
    payload: HttpStoreProjectSocialWelfareConditionsPayload
}) => axios.post(`projects/${urlParams.projectId}/social-welfare-conditions`, payload)

// get edit social welfare conditions

export const HttpEditProjectSocialWelfareConditionsPayload = HttpStoreProjectSocialWelfareConditionsPayload.partial()
export type HttpEditProjectSocialWelfareConditionsPayload = z.infer<
    typeof HttpEditProjectSocialWelfareConditionsPayload
>

export const httpEditProjectSocialWelfareConditions = ({
    urlParams,
    payload
}: {
    urlParams: {projectId: string}
    payload: HttpEditProjectSocialWelfareConditionsPayload
}) => axios.patch(`projects/${urlParams.projectId}/social-welfare-conditions`, payload)

//get medical record
export const HttpGetProjectClinicalPresentationsResponse = z.object({
    id: z.string(),
    cognitiveDisorders: ProjectClinicalPresentation.nullable(),
    behavioralDisorders: ProjectClinicalPresentation.nullable(),
    communicationDisorders: ProjectClinicalPresentation.nullable(),
    sensoryDeficit: ProjectClinicalPresentation.nullable(),
    stabilityLevel: StabilityLevel.nullable(),
    careNeeds: z.array(
        z.object({
            id: z.string(),
            name: ProjectClinicalCareNeed
        })
    )
})
export type HttpGetProjectClinicalPresentationsResponse = z.infer<typeof HttpGetProjectClinicalPresentationsResponse>

export const httpGetProjectClinicalPresentations = async ({urlParams}: {urlParams: {projectId: string}}) => {
    const response = await axios.get(`projects/${urlParams.projectId}/clinical-presentations`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetProjectClinicalPresentationsResponse
    })
}

export const HttpStoreProjectClinicalPresentationsPayload = HttpGetProjectClinicalPresentationsResponse.pick({
    cognitiveDisorders: true,
    behavioralDisorders: true,
    communicationDisorders: true,
    sensoryDeficit: true,
    stabilityLevel: true
}).extend({
    careNeeds: z.array(
        z.object({
            name: ProjectClinicalCareNeed
        })
    )
})
export type HttpStoreProjectClinicalPresentationsPayload = z.infer<typeof HttpStoreProjectClinicalPresentationsPayload>

export const httpStoreProjectClinicalPresentations = ({
    urlParams,
    payload
}: {
    urlParams: {projectId: string}
    payload: HttpStoreProjectClinicalPresentationsPayload
}) => axios.post(`projects/${urlParams.projectId}/clinical-presentations`, payload)

// edit project clinical presentations
export const HttpEditProjectClinicalPresentationsPayload = HttpStoreProjectClinicalPresentationsPayload.partial()
export type HttpEditProjectClinicalPresentationsPayload = z.infer<typeof HttpEditProjectClinicalPresentationsPayload>

export const httpEditProjectClinicalPresentations = ({
    urlParams,
    payload
}: {
    urlParams: {projectId: string}
    payload: HttpEditProjectClinicalPresentationsPayload
}) => axios.patch(`projects/${urlParams.projectId}/clinical-presentations`, payload)

// update project details
export const HttpEditProjectDetailsPayload = z
    .object({
        type: ProjectType.nullish(),
        sector: z.enum(['outpatient', 'domiciliary', 'mixed']).optional(),
        durationDays: z.number().nullish(),
        startDate: z.string().nullish(),
        expirationDate: z.string().nullish(),
        numberOfCycles: z.string().nullish(),
        specialistId: z.string().nullish(),
        sender: ProjectSender.nullish(),
        assistiveRegime: AssistiveRegimeEnum.nullish(),
        actualDays: z.number().nullish(),
        haveActualDays: z.boolean().optional(),
        livingConditions: ProjectPatientLivingCondition.nullish(),
        rehabilitationFacility: ProjectRehabilitationFacility.nullish(),
        suspensionStartDate: z.string().nullish(),
        suspensionEndDate: z.string().nullish(),
        suspensionReason: ProjectSuspendReason.nullish(),
        suspensionNotes: z.string().nullish(),
        openingTransmissionAt: z.string().nullish(),
        closingTransmissionAt: z.string().nullish(),
        progressStatus: ProjectProgressStatus.nullish(),
        endDate: z.string().nullish(),
        conclusionReason: z.enum(ProjectEndingReason.options).nullish()
    })
    .extend(NotificationPayload.shape)
export type HttpEditProjectDetailsPayload = z.infer<typeof HttpEditProjectDetailsPayload>

export interface HttpEditProjectDetailsOptions {
    urlParams: {projectId: string}
    payload: HttpEditProjectDetailsPayload
}

export const httpEditProjectDetails = ({urlParams, payload}: HttpEditProjectDetailsOptions) =>
    axios.patch(`projects/${urlParams.projectId}`, payload)

// Get project counters
export interface HttpGetProjectAccessCountersOptions {
    urlParams: {
        projectId: string
        accessTypeId: string
    }
}
export const httpGetProjectAccessCounters = async ({urlParams}: HttpGetProjectAccessCountersOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/access-types/${urlParams.accessTypeId}/counter`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectAccessCounters
    })
}

// get holidays
const HttpGetHolidaysResponse = z
    .object({
        date: z.string()
    })
    .array()
export type HttpGetHolidaysResponse = z.infer<typeof HttpGetHolidaysResponse>

export const httpGetHolidays = async ({urlParams}: {urlParams: {year: number}}) => {
    const response = await axios.get(`https://date.nager.at/api/v3/PublicHolidays/${urlParams.year}/it`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetHolidaysResponse
    })
}

// project siar transmission
export const HttpProjectExportSiarPayload = z.object({
    projectIds: z.array(z.string()),
    addRegistry: z.boolean(),
    exportType: z.enum(['open_project', 'close_project'])
})
export type HttpProjectExportSiarPayload = z.infer<typeof HttpProjectExportSiarPayload>

export const httpProjectExportSiar = ({payload}: {payload: HttpProjectExportSiarPayload}) =>
    axios.post('siar-xml-export', payload, {responseType: 'blob'}).then(response => response.data)

// generate fm22
export interface HttpGenerateFm22Options {
    urlParams: {
        projectId: string
    }
    payload: {
        signature: boolean
    }
}

export const httpGenerateFm22 = ({urlParams, payload}: HttpGenerateFm22Options) =>
    axios
        .post(`projects/${urlParams.projectId}/planned-accesses/generate-fm22`, payload, {responseType: 'blob'})
        .then(response => response.data)

// print siar
export interface HttpPrintSiarOptions {
    urlParams: {
        projectId: string
    }
    payload: {
        addSignature: boolean
        printType: 'open_project' | 'close_project'
    }
}

export const httpPrintSiar = ({urlParams, payload}: HttpPrintSiarOptions) =>
    axios
        .post(`projects/${urlParams.projectId}/siar-print`, payload, {responseType: 'blob'})
        .then(response => response.data)

interface HttpGetProjectValidatorsOptions {
    urlParams: {
        projectId: string
    }
}
const HttpGetProjectValidatorsResponse = z.object({
    projectData: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    }),
    otherData: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    }),
    scalesAndTests: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    }),
    diagnosisAndFacilities: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    }),
    disabilityProfile: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    }),
    plannedAccesses: z.object({
        fieldsToCompile: z.array(z.string()),
        isEmpty: z.boolean()
    })
})
export type HttpGetProjectValidatorsResponse = z.infer<typeof HttpGetProjectValidatorsResponse>

export const httpGetProjectValidators = async ({urlParams}: HttpGetProjectValidatorsOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/validators`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetProjectValidatorsResponse
    })
}

// bulk transmission
export interface HttpBulkTransmissionOptions {
    payload: {
        projectIds: string[]
        type: 'opening' | 'closing'
        transmissionDate: string
    }
}

export const httpBulkTransmission = ({payload}: HttpBulkTransmissionOptions) =>
    axios.post('projects/bulk-transmission', payload)
